import type { Profile, ProfileUpdatePayload } from '@/lib/types/auth'
import { API_BASE } from '@/lib/utils'

// Narrowing guard for the trust boundary at `res.json()`: without a schema
// library (no Zod in this project) we at least confirm the required fields
// are present with the right primitive types before trusting the shape.
function isProfile(value: unknown): value is Profile {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.username === 'string' &&
    typeof candidate.total_predictions === 'number' &&
    typeof candidate.correct_predictions === 'number'
  )
}

function parseProfileResponse(json: unknown): Profile {
  const data = typeof json === 'object' && json !== null ? (json as { data?: unknown }).data : undefined
  if (!isProfile(data)) {
    throw new Error('Invalid profile response shape')
  }
  return data
}

// FastAPI's HTTPException bodies look like { "detail": "..." }. Falls back to
// the HTTP status if the body isn't in that shape (e.g. an upstream 502).
async function errorMessage(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json()
    if (typeof body === 'object' && body !== null && typeof (body as { detail?: unknown }).detail === 'string') {
      return (body as { detail: string }).detail
    }
  } catch {
    // Body wasn't JSON — fall through to the status-based message.
  }
  return String(res.status)
}

export async function getMyProfile(token: string, signal?: AbortSignal): Promise<Profile> {
  const res = await fetch(`${API_BASE}/profiles/me`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  })
  if (!res.ok) throw new Error(String(res.status))
  return parseProfileResponse(await res.json())
}

export async function getProfileByUsername(username: string): Promise<Profile> {
  const res = await fetch(`${API_BASE}/profiles/${username}`)
  if (!res.ok) throw new Error(String(res.status))
  return parseProfileResponse(await res.json())
}

export async function updateMyProfile(token: string, payload: ProfileUpdatePayload): Promise<Profile> {
  const res = await fetch(`${API_BASE}/profiles/me`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
  if (res.status === 409) throw new Error('USERNAME_TAKEN')
  if (!res.ok) throw new Error(await errorMessage(res))
  return parseProfileResponse(await res.json())
}

export async function deleteMyProfile(token: string): Promise<void> {
  const res = await fetch(`${API_BASE}/profiles/me`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(await errorMessage(res))
}
